import React from 'react';
import { FJ712 } from '../../../services/c209Api';

interface Tab5Props {
  colors: Partial<FJ712>[];
  selectedColorRow: number;
  isEditing: boolean;
  setSelectedColorRow: (idx: number) => void;
  setColors: React.Dispatch<React.SetStateAction<Partial<FJ712>[]>>;
  addColorRow: () => void;
  deleteColorRow: () => void;
}

/** Tab 5: 顏色明細 (Page3 in spec - FJ712 grid)
 *  BR-U01: Column2(fj712_02), Column6(fj712_15 CN name), Column7(fj712_16 EN name) disabled
 *  BR-V10: color code required; BR-V11: ZZZ requires print color
 *  BR-F10/F11: add/delete rows, max 99
 *  BR-V14: color lookup auto-fills fj712_15/fj712_16
 */
const Tab5ColorGrid: React.FC<Tab5Props> = React.memo(({
  colors, selectedColorRow, isEditing,
  setSelectedColorRow, setColors,
  addColorRow, deleteColorRow,
}) => {
  const updateField = (idx: number, key: keyof FJ712, value: FJ712[keyof FJ712]) => {
    setColors(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  return (
    <div className="tab-content-section">
      <div className="grid-header">
        <h3 className="section-title">顏色明細</h3>
        <span style={{ fontSize: 13, color: '#666', marginLeft: 12 }}>總項數: {colors.length}</span>
        {isEditing && (
          <div className="grid-actions">
            <button
              className="btn btn-sm"
              onClick={addColorRow}
              disabled={colors.length >= 99}
            >
              新增列
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={deleteColorRow}
              disabled={colors.length === 0}
            >
              刪除列
            </button>
          </div>
        )}
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 50 }}>項次</th>
              <th style={{ width: 80 }}>色號</th>
              <th style={{ width: 100 }}>數量</th>
              <th style={{ width: 120 }}>印花配色</th>
              <th style={{ width: 120 }}>中文色名</th>
              <th style={{ width: 120 }}>英文色名</th>
            </tr>
          </thead>
          <tbody>
            {colors.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>無資料</td></tr>
            ) : (
              colors.map((row, i) => (
                <tr
                  key={i}
                  className={i === selectedColorRow ? 'row-selected' : ''}
                  onClick={() => setSelectedColorRow(i)}
                >
                  {/* Column2: seq no (always disabled, BR-U01) */}
                  <td>{row.fj712_02 || i + 1}</td>
                  {/* Color code (editable, BR-V14 lookup on blur) */}
                  <td>
                    <input
                      type="text"
                      value={row.fj712_11 || ''}
                      onChange={e => updateField(i, 'fj712_11', e.target.value)}
                      disabled={!isEditing}
                      style={{ width: '100%' }}
                    />
                  </td>
                  {/* Quantity [T-F03] */}
                  <td>
                    <input
                      type="number"
                      value={row.fj712_12 || 0}
                      onChange={e => updateField(i, 'fj712_12', parseFloat(e.target.value) || 0)}
                      disabled={!isEditing}
                      style={{ width: '100%' }}
                    />
                  </td>
                  {/* Print color match (editable when ZZZ) */}
                  <td>
                    <input
                      type="text"
                      value={row.fj712_13 || ''}
                      onChange={e => updateField(i, 'fj712_13', e.target.value)}
                      disabled={!isEditing}
                      style={{ width: '100%' }}
                    />
                  </td>
                  {/* Chinese color name (disabled, auto from lookup, BR-U01) */}
                  <td>
                    <input
                      type="text"
                      value={row.fj712_15 || ''}
                      disabled
                      style={{ width: '100%' }}
                    />
                  </td>
                  {/* English color name (disabled, auto from lookup, BR-U01) */}
                  <td>
                    <input
                      type="text"
                      value={row.fj712_16 || ''}
                      disabled
                      style={{ width: '100%' }}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

Tab5ColorGrid.displayName = 'Tab5ColorGrid';

export default Tab5ColorGrid;
